import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { RefreshCw, Search, Building2, User, X, ExternalLink, Flame } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function MobileEngage() {
  const { reloadBadges } = useOutletContext() || {};
  const [items, setItems] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/linkedin-engage/engagements?status=pending_review");
      const list = data.engagements || [];
      setItems(list);
      const d = {}; list.forEach((e) => { d[e.id] = e.draft_comment || ""; });
      setDrafts(d);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/linkedin-engage/scan");
      if (!data.job_id) { setScanning(false); return load(); }
      toast.info("Scanning for viral posts…");
      const t = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/linkedin-engage/scan/${data.job_id}`);
          if (job.status === "done" || job.status === "failed") {
            clearInterval(t); setScanning(false);
            if (job.status === "done") toast.success("Scan complete"); else toast.error("Scan failed");
            load(); reloadBadges?.();
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) { setScanning(false); toast.error(e?.response?.data?.detail || "Could not scan"); }
  };

  const approve = async (e, asPage) => {
    setBusyId(e.id);
    try {
      const { data } = await api.post(`/linkedin-engage/engagements/${e.id}/approve`,
        { text: drafts[e.id], as_page: asPage });
      toast.success(data.posted_as === "page" ? "Commented as the Europacific page" : "Commented as you (Rok)");
      await load(); reloadBadges?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d === "no_company_page_configured" ? "Set the company page in the full app first." : (d || "Could not post"));
    }
    setBusyId(null);
  };

  const reject = async (e) => {
    setBusyId(e.id);
    try { await api.post(`/linkedin-engage/engagements/${e.id}/reject`); await load(); reloadBadges?.(); }
    catch { toast.error("Could not dismiss"); }
    setBusyId(null);
  };

  return (
    <div className="space-y-4" data-testid="m-engage">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Page engagement</h1>
        <button onClick={scan} disabled={scanning}
          className="flex items-center gap-1.5 text-xs bg-card border border-border rounded-lg px-2.5 py-1.5 active:bg-muted/40"
          data-testid="m-engage-scan">
          {scanning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {scanning ? "Scanning" : "Scan"}
        </button>
      </div>
      <p className="text-[0.72rem] text-muted-foreground -mt-2">
        Comment on viral posts to grow visibility. Choose <b>as the page</b> or <b>as you</b>.
      </p>

      {items === null && <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>}
      {items && items.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-12" data-testid="m-engage-empty">
          Nothing to review. Tap <b>Scan</b> to find fresh viral posts.
        </div>
      )}

      {items && items.map((e) => (
        <div key={e.id} className="bg-card border border-border rounded-xl p-3.5 space-y-2.5" data-testid={`m-engage-card-${e.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{e.author_name || "Industry post"}</div>
              <div className="text-[0.7rem] text-muted-foreground flex items-center gap-1.5">
                <Flame className="w-3 h-3 text-orange-400" /> {e.reactions || 0} reactions
                {e.suggested_identity === "page" && <span className="text-signal-500">· suggests page</span>}
              </div>
            </div>
            {e.post_url && (
              <a href={e.post_url} target="_blank" rel="noreferrer" className="text-muted-foreground shrink-0">
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
          {e.post_excerpt && <p className="text-[0.72rem] text-muted-foreground line-clamp-3">{e.post_excerpt}</p>}
          <textarea
            value={drafts[e.id] || ""}
            onChange={(ev) => setDrafts((p) => ({ ...p, [e.id]: ev.target.value }))}
            rows={3}
            className="w-full text-sm bg-background border border-border rounded-lg p-2.5 resize-none focus:outline-none focus:border-signal-500"
            placeholder="Your comment…"
            data-testid={`m-engage-draft-${e.id}`}
          />
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => approve(e, true)} disabled={busyId === e.id || !drafts[e.id]?.trim()}
              className="flex items-center justify-center gap-1 text-xs font-medium bg-signal-500 text-background rounded-lg py-2 disabled:opacity-50"
              data-testid={`m-engage-page-${e.id}`}>
              <Building2 className="w-3.5 h-3.5" /> Page
            </button>
            <button onClick={() => approve(e, false)} disabled={busyId === e.id || !drafts[e.id]?.trim()}
              className="flex items-center justify-center gap-1 text-xs font-medium bg-card border border-border rounded-lg py-2 disabled:opacity-50"
              data-testid={`m-engage-me-${e.id}`}>
              <User className="w-3.5 h-3.5" /> As me
            </button>
            <button onClick={() => reject(e)} disabled={busyId === e.id}
              className="flex items-center justify-center gap-1 text-xs text-muted-foreground bg-card border border-border rounded-lg py-2 disabled:opacity-50"
              data-testid={`m-engage-skip-${e.id}`}>
              <X className="w-3.5 h-3.5" /> Skip
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
